import type { ClipboardEvent, KeyboardEvent, ReactNode } from "react";

type AdminFieldSize = "default" | "compact";

const inputSizeClass: Record<AdminFieldSize, string> = {
  default: "rounded-xl px-3.5 py-2.5 text-[15px]",
  compact: "rounded-lg px-2.5 py-1.5 text-sm",
};

const inputClassNameBase =
  "w-full border border-slate-200 bg-white font-onest text-slate-800 shadow-sm transition-colors placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20";

const labelSizeClass: Record<AdminFieldSize, string> = {
  default: "mb-1.5 text-sm",
  compact: "mb-1 text-xs",
};

export function FieldLabel({
  htmlFor,
  size = "default",
  children,
}: {
  htmlFor?: string;
  size?: AdminFieldSize;
  children: ReactNode;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={`block font-onest font-medium text-slate-700 ${labelSizeClass[size]}`}
    >
      {children}
    </label>
  );
}

export function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type = "text",
  size = "default",
  className = "",
  onBlur,
  onKeyDown,
  onPaste,
  autoFocus,
  disabled,
  autoComplete,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  size?: AdminFieldSize;
  className?: string;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  autoComplete?: string;
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      autoFocus={autoFocus}
      className={`${inputClassNameBase} ${inputSizeClass[size]} ${
        disabled ? "cursor-not-allowed bg-slate-50 text-slate-400" : ""
      } ${className}`.trim()}
    />
  );
}

export function TextArea({
  id,
  value,
  onChange,
  placeholder,
  rows = 3,
  size = "default",
  maxLength,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  size?: AdminFieldSize;
  maxLength?: number;
}) {
  return (
    <textarea
      id={id}
      value={value}
      rows={rows}
      maxLength={maxLength}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputClassNameBase} ${inputSizeClass[size]} resize-y ${
        size === "compact" ? "min-h-[64px]" : "min-h-[88px]"
      }`}
    />
  );
}

export function SelectInput({
  id,
  value,
  onChange,
  size = "default",
  disabled,
  ariaLabel,
  children,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  size?: AdminFieldSize;
  disabled?: boolean;
  ariaLabel?: string;
  children: ReactNode;
}) {
  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputClassNameBase} ${inputSizeClass[size]} disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400`}
    >
      {children}
    </select>
  );
}

export function Checkbox({
  id,
  checked,
  onChange,
  label,
  size = "default",
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  size?: AdminFieldSize;
}) {
  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-center gap-2 font-onest text-slate-700 ${
        size === "compact" ? "text-xs" : "text-sm"
      }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className={`rounded border-slate-300 text-brand-500 focus:ring-brand-500/30 ${
          size === "compact" ? "h-3.5 w-3.5" : "h-4 w-4"
        }`}
      />
      {label}
    </label>
  );
}

export function SwitchControl({
  id,
  checked,
  onChange,
  compact,
  ariaLabel,
  checkedClassName = "bg-amber-500",
  uncheckedClassName = "bg-emerald-500",
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  compact?: boolean;
  ariaLabel?: string;
  checkedClassName?: string;
  uncheckedClassName?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={[
        "relative inline-flex shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30",
        compact ? "h-6 w-10" : "h-7 w-12",
        checked ? checkedClassName : uncheckedClassName,
      ].join(" ")}
    >
      <span
        className={[
          "pointer-events-none inline-block rounded-full bg-white shadow transition-transform",
          compact ? "h-4 w-4 translate-y-1" : "h-5 w-5 translate-y-1",
          checked
            ? compact
              ? "translate-x-5"
              : "translate-x-6"
            : "translate-x-1",
        ].join(" ")}
      />
    </button>
  );
}

export function ToggleSwitch({
  id,
  checked,
  onChange,
  label,
  description,
  compact,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  compact?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <label
          htmlFor={id}
          className={`font-onest font-medium text-slate-800 ${
            compact ? "text-sm" : "text-sm"
          }`}
        >
          {label}
        </label>
        {description ? (
          <p
            className={`mt-0.5 font-onest text-slate-500 ${
              compact ? "text-[11px] leading-snug" : "text-xs"
            }`}
          >
            {description}
          </p>
        ) : null}
      </div>
      <SwitchControl
        id={id}
        checked={checked}
        onChange={onChange}
        compact={compact}
      />
    </div>
  );
}

const inputClassName = `${inputClassNameBase} ${inputSizeClass.default}`;
const labelClassName = `block font-onest font-medium text-slate-700 ${labelSizeClass.default}`;

export { inputClassName, labelClassName };
